// Runtime feature detection. Surfaces whether the current browser supports
// everything the player needs to run (and play) reliably. Shown on the pairing
// screen so a tech installing a new screen can diagnose an unsupported browser.

export type CapabilityId =
  | 'indexeddb'
  | 'serviceWorker'
  | 'websocket'
  | 'video'
  | 'cache'
  | 'storage';

export interface Capability {
  id: CapabilityId;
  ok: boolean;
  // Required capabilities must work or the player cannot run; the rest are
  // progressive enhancements (offline polish, diagnostics).
  required: boolean;
}

function canPlayMp4(): boolean {
  try {
    const v = document.createElement('video');
    return v.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '';
  } catch {
    return false;
  }
}

// Smart-TV / set-top browsers typically expose a single hardware video decoder,
// so the player falls back to single-player mode (one video at a time, no
// preload) on these. Detected purely from the user agent.
export function isTvBrowser(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /tizen|webos|web0s|smart-?tv|netcast|hbbtv|bravia|viera|aquos|crkey|appletv/i.test(
      navigator.userAgent,
    )
  );
}

export function getCapabilities(): Capability[] {
  return [
    { id: 'indexeddb', ok: 'indexedDB' in window, required: true },
    { id: 'video', ok: canPlayMp4(), required: true },
    { id: 'websocket', ok: 'WebSocket' in window, required: true },
    { id: 'serviceWorker', ok: 'serviceWorker' in navigator, required: false },
    { id: 'cache', ok: 'caches' in window, required: false },
    { id: 'storage', ok: typeof navigator.storage?.estimate === 'function', required: false },
  ];
}

// True when every required capability is present.
export function isRunnable(caps: Capability[] = getCapabilities()): boolean {
  return caps.every((c) => !c.required || c.ok);
}
